import React, { useState } from "react";
import {
  Award,
  BookOpen,
  CalendarRange,
  ChevronDown,
  Clock,
  FileDown,
  Gauge,
  Info,
  Medal,
  Search,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  X,
} from "lucide-react";
import { Bar } from "react-chartjs-2";
import { exportAnyJsonToExcel } from "../../../Utils/exportAnyJsonToExcel";
import { toast } from "sonner";
import { formatDate } from "../../../Utils/dateFormatter";

const DriverPointsSection = ({
  pointsLoading,
  driverPoints,

  pointsYear,
  setPointsYear,
  currentYear,

  selectedWeek,
  selectedWeekId,
  setSelectedWeekId,

  weekSearch,
  setWeekSearch,

  showWeekDropdown,
  setShowWeekDropdown,

  filteredWeekOptions,
  handlePointsWeekSelect,
  buildYearWeeks,

  setDailyPoints,
  fetchDriverDailyPerformancePoints,
  driverId,

  totalPositivePoints,
  totalNegativePoints,
  pointsTier,
  pointCategories,

  dailyPoints,
  dailyPointsLoading,
  dailyPointsChartData,
  bestDailyPoint,
  worstDailyPoint,

  getPointTone,
  getFactorMeta,

  masterPointsLoading,
  masterPointsData,

  showPointsInfo,
  setShowPointsInfo,

  Badge,
  formatMinutes,

  weeklyPoints = [],
  weeklyPointsLoading = false,

  handleUpdateDressCodePoints,
  dressCodePointsLoading = false,
}) => {
  const totalDailyPoints = dailyPoints.reduce(
    (sum, item) => sum + Number(item?.dailyPerformmancePoints || 0),
    0,
  );

  const totalWeeklyDailyPoints = weeklyPoints.reduce(
    (sum, item) => sum + Number(item?.dailyPoints || 0),
    0,
  );

  const latestWeeklyPoint =
    weeklyPoints?.length > 0 ? weeklyPoints[weeklyPoints.length - 1] : null;

  const topTotalPoints =
    latestWeeklyPoint?.totalPoints ?? driverPoints?.points ?? 0;

  const handleWeeklyPointsExport = () => {
    if (!weeklyPoints?.length) {
      return;
    }

    const exportData = weeklyPoints.map((item, index) => ({
      "S.No": index + 1,
      "Performance ID": item?.pk_performmance_id || "N/A",
      "Total Points": Number(item?.totalPoints || 0),
      "Daily Points": Number(item?.dailyPoints || 0),
      Status: Number(item?.dailyPoints || 0) >= 0 ? "Added" : "Deducted",
    }));

    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `Driver_Weekly_Points_${driverId}_${selectedWeek?.fromDate || ""
        }_to_${selectedWeek?.toDate || ""}`,
      sheetName: "Weekly Points",
    });
  };

  const handleDailyPointsExport = () => {
    if (!dailyPoints?.length) {
      return;
    }

    const exportData = dailyPoints.map((item, index) => ({
      "S.No": item?.rowNumber || index + 1,
      Date: formatDate(item?.created_on),
      "Daily Points": Number(item?.dailyPerformmancePoints || 0),
      "Shift Start": item?.shift_start || "N/A",
      "Shift End": item?.shift_end || "N/A",
      "Duty Duration": formatMinutes(Number(item?.dutyDuration || 0)),
    }));

    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `Driver_Daily_Points_${driverId}_${selectedWeek?.fromDate || ""
        }_to_${selectedWeek?.toDate || ""}`,
      sheetName: "Daily Points",
    });
  };

  const [editingDressCode, setEditingDressCode] = useState(false);
  const [dressCodeValue, setDressCodeValue] = useState(
    driverPoints?.dress_code_points ?? 0
  );

const startDressCodeEdit = () => {
  const currentValue = Number(driverPoints?.dress_code_points || 0);

  if (currentValue !== 0) {
    return;
  }

  setDressCodeValue(currentValue);
  setEditingDressCode(true);
};

  const cancelDressCodeEdit = () => {
    setDressCodeValue(driverPoints?.dress_code_points ?? 0);
    setEditingDressCode(false);
  };

  const saveDressCodePoints = async () => {
  const currentValue = Number(driverPoints?.dress_code_points || 0);

  if (currentValue !== 0) {
    toast.error("Dress code points can be updated only when current points are 0");
    setEditingDressCode(false);
    return;
  }

  const value = Number(dressCodeValue || 0);

  const success = await handleUpdateDressCodePoints?.(value);

  if (success) {
    setEditingDressCode(false);
  }
};
  return (
    <div className="space-y-6">
      {pointsLoading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-36 rounded-2xl border bg-white animate-pulse"
            />
          ))}
        </div>
      ) : !driverPoints ? (
        <div className="rounded-2xl border bg-white p-8 text-center">
          <Trophy className="mx-auto mb-3 h-10 w-10 text-gray-300" />

          <h3 className="text-lg font-semibold text-gray-700">
            No points found
          </h3>

          <p className="text-sm text-gray-500">
            No performance points available for this driver.
          </p>
        </div>
      ) : (
        <>
          {/* WEEK FILTER */}
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Daily Points Week Filter
                </h3>

                <p className="text-sm text-gray-500">
                  Daily performance points are calculated Monday to Sunday.
                  Select a week to load exact data.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Year
                  </label>

                  <select
                    value={pointsYear}
                    onChange={(e) => {
                      const year = Number(e.target.value);
                      const weeks = buildYearWeeks(year);

                      setPointsYear(year);
                      setDailyPoints([]);

                      if (weeks[0]) {
                        handlePointsWeekSelect(weeks[0]);
                      }
                    }}
                    className="h-11 rounded-xl border bg-white px-3 text-sm font-medium outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  >
                    {Array.from({ length: 6 }).map((_, index) => {
                      const year = currentYear - index;

                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="relative min-w-[320px]">
                  <label className="mb-1 block text-xs font-semibold text-gray-500">
                    Select Week
                  </label>

                  <button
                    type="button"
                    onClick={() => setShowWeekDropdown((prev) => !prev)}
                    className="flex h-11 w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 text-left text-sm font-medium outline-none hover:border-orange-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  >
                    <span className="flex items-center gap-2 truncate">
                      <CalendarRange size={16} className="text-orange-600" />
                      {selectedWeek?.label || "Select week"}
                    </span>

                    <ChevronDown size={16} className="text-gray-400" />
                  </button>

                  {showWeekDropdown && (
                    <div className="absolute right-0 z-30 mt-2 w-full overflow-hidden rounded-2xl border bg-white shadow-xl">
                      <div className="border-b p-3">
                        <div className="flex items-center gap-2 rounded-xl border bg-gray-50 px-3">
                          <Search size={16} className="text-gray-400" />

                          <input
                            value={weekSearch}
                            onChange={(e) => setWeekSearch(e.target.value)}
                            placeholder="Search week, date, month..."
                            className="h-10 w-full bg-transparent text-sm outline-none"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="max-h-72 overflow-y-auto p-2">
                        {filteredWeekOptions.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-400">
                            No week found
                          </div>
                        ) : (
                          filteredWeekOptions.map((week) => {
                            const active = week.id === selectedWeekId;

                            return (
                              <button
                                key={week.id}
                                type="button"
                                onClick={() => handlePointsWeekSelect(week)}
                                className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${
                                  active
                                    ? "bg-orange-50 text-orange-700"
                                    : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <div>
                                  <p className="font-semibold">{week.label}</p>

                                  <p className="text-xs text-gray-400">
                                    API Range: {week.fromDate} to {week.toDate}
                                  </p>
                                </div>

                                {active && (
                                  <span className="rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white">
                                    Selected
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {selectedWeek && (
              <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                Showing daily points from{" "}
                <span className="font-bold">
                  {formatDate(selectedWeek.fromDate)}
                </span>{" "}
                to{" "}
                <span className="font-bold">
                  {formatDate(selectedWeek.toDate)}
                </span>
                .
              </div>
            )}

            {/* WEEKLY + DAILY TABLES SIDE BY SIDE */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 mt-4">
              {/* WEEKLY POINTS TABLE */}
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Weekly Performance Points
                    </h3>

                    <p className="text-xs text-gray-500">
                      Selected week performance points for driver{" "}
                      <span className="font-semibold">{driverId}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 whitespace-nowrap shrink-0">
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2 py-1 text-xs font-bold text-orange-700">
                      <Trophy size={14} />
                      Latest Total Points:
                      <span className="rounded-full bg-white px-1.5 py-0.5 text-sm font-extrabold text-gray-900 shadow-sm">
                        {topTotalPoints}
                      </span>
                    </div>
                    {selectedWeek && (
                      <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-bold text-orange-700">
                        {formatDate(selectedWeek.fromDate)} →{" "}
                        {formatDate(selectedWeek.toDate)}
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={handleWeeklyPointsExport}
                      disabled={!weeklyPoints?.length}
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                        weeklyPoints?.length
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "cursor-not-allowed bg-gray-100 text-gray-400"
                      }`}
                    >
                      <FileDown size={14} />
                      Excel
                    </button>
                  </div>
                </div>

                {weeklyPointsLoading ? (
                  <div className="p-5 text-sm text-gray-500">
                    Loading weekly points...
                  </div>
                ) : weeklyPoints?.length > 0 ? (
                  <div className="max-h-[420px] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-orange-400 text-left text-white">
                        <tr>
                          <th className="px-4 py-3">#</th>
                          <th className="px-4 py-3">Performance ID</th>
                          <th className="px-4 py-3">Total Points</th>
                          <th className="px-4 py-3">Daily Points</th>
                          <th className="px-4 py-3">Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {weeklyPoints.map((item, index) => {
                          const dailyPointValue = Number(
                            item?.dailyPoints || 0,
                          );
                          const tone = getPointTone(dailyPointValue);

                          return (
                            <tr
                              key={item?.pk_performmance_id || index}
                              className="border-t hover:bg-orange-50/40 transition"
                            >
                              <td className="px-4 py-3 font-medium text-gray-500">
                                {index + 1}
                              </td>

                              <td className="px-4 py-3 font-semibold text-gray-800">
                                {item?.pk_performmance_id || "N/A"}
                              </td>

                              <td className="px-4 py-3 font-bold text-gray-900">
                                {Number(item?.totalPoints || 0)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold ${tone.bg} ${tone.border} ${tone.text}`}
                                >
                                  {tone.icon}
                                  {dailyPointValue > 0
                                    ? `+${dailyPointValue}`
                                    : dailyPointValue}
                                </span>
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                                    dailyPointValue >= 0
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-red-50 text-red-700"
                                  }`}
                                >
                                  {dailyPointValue >= 0 ? "Added" : "Deducted"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-5 text-sm text-gray-500">
                    No weekly points found for selected week.
                  </div>
                )}
              </div>

              {/* DAILY POINTS TABLE */}
              <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Daily Performance Details
                    </h3>

                    <p className="text-xs text-gray-500">
                      Shift timing, duty duration and daily points
                    </p>

                    {/* <p className="mt-1 text-xs font-semibold text-gray-700">
          Total Daily Points:{" "}
          <span
            className={
              totalDailyPoints >= 0 ? "text-emerald-700" : "text-red-700"
            }
          >
            {totalDailyPoints > 0 ? `+${totalDailyPoints}` : totalDailyPoints}
          </span>
        </p> */}
                  </div>

                  <button
                    type="button"
                    onClick={handleDailyPointsExport}
                    disabled={!dailyPoints?.length}
                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                      dailyPoints?.length
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "cursor-not-allowed bg-gray-100 text-gray-400"
                    }`}
                  >
                    <FileDown size={14} />
                    Excel
                  </button>
                </div>

                {dailyPointsLoading ? (
                  <div className="p-5 text-sm text-gray-500">
                    Loading daily points...
                  </div>
                ) : dailyPoints.length === 0 ? (
                  <div className="p-5 text-sm text-gray-500">
                    No daily points found for selected week.
                  </div>
                ) : (
                  <div className="max-h-[420px] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-orange-400 text-left text-white">
                        <tr>
                          <th className="px-4 py-3">#</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Daily Points</th>
                          <th className="px-4 py-3">Shift Start</th>
                          <th className="px-4 py-3">Shift End</th>
                          <th className="px-4 py-3">Duty Duration</th>
                        </tr>
                      </thead>

                      <tbody>
                        {dailyPoints.map((item, index) => {
                          const tone = getPointTone(
                            item.dailyPerformmancePoints,
                          );

                          return (
                            <tr
                              key={item.rowNumber || index}
                              className="border-t hover:bg-orange-50/40 transition"
                            >
                              <td className="px-4 py-3 font-medium text-gray-500">
                                {item.rowNumber || index + 1}
                              </td>

                              <td className="px-4 py-3 font-semibold text-gray-800">
                                {formatDate(item.created_on)}
                              </td>

                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-bold ${tone.bg} ${tone.border} ${tone.text}`}
                                >
                                  {tone.icon}
                                  {tone.prefix}
                                  {Number(item.dailyPerformmancePoints || 0)}
                                </span>
                              </td>

                              <td className="px-4 py-3 text-gray-700">
                                {item.shift_start || "N/A"}
                              </td>

                              <td className="px-4 py-3 text-gray-700">
                                {item.shift_end || "N/A"}
                              </td>

                              <td className="px-4 py-3">
                                <Badge className="border border-blue-200 bg-blue-50 text-blue-700">
                                  <Clock size={14} />
                                  {formatMinutes(
                                    Number(item.dutyDuration || 0),
                                  )}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* TOP SUMMARY */}
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="lg:col-span-2 rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-orange-700">
                    Weekly Performance Score
                  </p>

                  <h2 className="mt-2 text-5xl font-extrabold text-gray-900">
                    {topTotalPoints}
                    <span className="ml-2 text-base font-semibold text-gray-400">
                      pts
                    </span>
                  </h2>

                  <div
                    className={`mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${pointsTier.cls}`}
                  >
                    <Medal size={16} />
                    {pointsTier.label} • {pointsTier.message}
                  </div>
                </div>

                <div
                  className="rounded-2xl bg-orange-500 p-4 text-white shadow-lg shadow-orange-200 cursor-pointer"
                  onClick={() => setShowPointsInfo(true)}
                >
                  <Trophy size={34} />
                </div>
              </div>

              <div className="mt-6">
                <div className="mb-2 flex justify-between text-xs font-medium text-gray-500">
                  <span>0</span>
                  <span>Target 1000</span>
                </div>

                <div className="h-3 overflow-hidden rounded-full bg-orange-100">
                  <div
                    className="h-full rounded-full bg-orange-500 transition-all duration-700"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.max(0, (Number(topTotalPoints || 0) / 1000) * 100),
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-2xl border bg-white p-3">
                  <p className="text-gray-500">From</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(driverPoints.from_date)}
                  </p>
                </div>

                <div className="rounded-2xl border bg-white p-3">
                  <p className="text-gray-500">To</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(driverPoints.to_date)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Positive Points</p>

                  <h3 className="mt-2 text-3xl font-bold text-emerald-700">
                    +{totalPositivePoints}
                  </h3>
                </div>

                <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                  <TrendingUp size={28} />
                </div>
              </div>

              <p className="mt-4 text-xs text-gray-500">
                Points earned from good behaviour like duty timing, attendance,
                and order performance.
              </p>
            </div>

            <div className="rounded-3xl border bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Negative Points</p>

                  <h3 className="mt-2 text-3xl font-bold text-red-700">
                    {totalNegativePoints}
                  </h3>
                </div>

                <div className="rounded-2xl bg-red-50 p-3 text-red-700">
                  <TrendingDown size={28} />
                </div>
              </div>

              <p className="mt-4 text-xs text-gray-500">
                Points deducted because of declined, missed, delayed, or poor
                performance cases.
              </p>
            </div>
          </div>

          {/* CATEGORY BREAKDOWN + DAILY SUMMARY */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2 rounded-3xl border bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Points Breakdown
                  </h3>

                  <p className="text-sm text-gray-500">
                    Category-wise score impact for this driver
                  </p>
                </div>

                <div className="rounded-xl bg-orange-50 p-2 text-orange-600">
                  <Gauge size={22} />
                </div>
              </div>

              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                {pointCategories.map((item) => {
                  const tone = getPointTone(item.value);
                  const value = Number(item.value || 0);
                  const isDressCode = item.key === "dress_code_points";
                  const canEditDressCode = isDressCode && value === 0;

                  return (
                    <div
                      key={item.key}
                      className={`rounded-xl border ${tone.border} bg-white p-3 shadow-sm transition hover:shadow-md`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone.bg} ${tone.text}`}
                          >
                            {item.icon}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-xs font-bold text-gray-900">
                              {item.label}
                            </p>

                            <p className="mt-0.5 truncate text-[10px] font-medium text-gray-400">
                              {item.key}
                            </p>
                          </div>
                        </div>

                        {!editingDressCode || !canEditDressCode ? (
                          <div className="shrink-0 text-right">
                            <div
                              className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-extrabold ${tone.bg} ${tone.text}`}
                            >
                              {tone.icon}
                              {tone.prefix}
                              {value}
                            </div>
                          </div>
                        ) : (
                          <div className="shrink-0">
                            <div className="flex items-center gap-1 rounded-lg border border-orange-100 bg-orange-50 p-1">
                              <input
                                type="number"
                                value={dressCodeValue}
                                onChange={(e) =>
                                  setDressCodeValue(e.target.value)
                                }
                                className="h-7 w-12 rounded-md border border-orange-100 bg-white px-1 text-center text-xs font-bold text-gray-900 outline-none focus:border-orange-400"
                              />

                              <button
                                type="button"
                                onClick={saveDressCodePoints}
                                disabled={dressCodePointsLoading}
                                className="h-7 rounded-md bg-orange-500 px-2 text-[10px] font-bold text-white disabled:opacity-60"
                              >
                                {dressCodePointsLoading ? "..." : "Save"}
                              </button>

                              <button
                                type="button"
                                onClick={cancelDressCodeEdit}
                                disabled={dressCodePointsLoading}
                                className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-gray-500 disabled:opacity-60"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-2 flex items-center justify-between">
                        <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={`h-full rounded-full ${
                              value >= 0 ? "bg-emerald-500" : "bg-red-500"
                            }`}
                            style={{
                              width: `${Math.min(100, Math.abs(value) * 2)}%`,
                            }}
                          />
                        </div>

                        {canEditDressCode && !editingDressCode && (
                          <button
                            type="button"
                            onClick={startDressCodeEdit}
                            className="ml-2 shrink-0 rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-[10px] font-bold text-orange-700 hover:bg-orange-100"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">
                Daily Summary
              </h3>

              <p className="text-sm text-gray-500">
                Selected date range performance
              </p>

              <div className="mt-5 space-y-3">
                <div className="rounded-2xl border bg-gray-50 p-4">
                  <p className="text-sm text-gray-500">Days Found</p>

                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {dailyPoints.length}
                  </p>
                </div>

                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-sm text-emerald-700">Best Day</p>

                  <p className="mt-1 text-2xl font-bold text-emerald-800">
                    {bestDailyPoint}
                  </p>
                </div>

                <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-700">Worst Day</p>

                  <p className="mt-1 text-2xl font-bold text-red-800">
                    {worstDailyPoint}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* DAILY CHART */}
          <div className="flex flex-col gap-3 border-b bg-gray-50 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Daily Performance Details
              </h3>

              <p className="text-xs text-gray-500">
                Shift timing, duty duration and daily points
              </p>

              <p className="mt-1 text-xs font-semibold text-gray-700">
                Total Daily Points:{" "}
                <span
                  className={
                    totalDailyPoints >= 0 ? "text-emerald-700" : "text-red-700"
                  }
                >
                  {totalDailyPoints > 0
                    ? `+${totalDailyPoints}`
                    : totalDailyPoints}
                </span>
              </p>
            </div>

            <button
              type="button"
              onClick={handleDailyPointsExport}
              disabled={!dailyPoints?.length}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                dailyPoints?.length
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "cursor-not-allowed bg-gray-100 text-gray-400"
              }`}
            >
              <FileDown size={14} />
              Excel
            </button>
          </div>
        </>
      )}

      {/* POINT SYSTEM CTA */}
      <div className="rounded-3xl border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-orange-500 p-3 text-white shadow-sm">
              <BookOpen size={22} />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Driver Point System
              </h3>

              <p className="text-sm text-gray-500">
                View how points are added or deducted for attendance, delivery
                time, reliability, acknowledgement, duty timing and successful
                orders.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowPointsInfo(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
          >
            <Info size={16} />
            View Point Rules
          </button>
        </div>
      </div>

      {/* POINT RULES MODAL */}
      {showPointsInfo && (
        <div
          className="fixed inset-0 z-[9999] flex min-h-screen w-screen items-center justify-center bg-black/60 p-4"
          onClick={() => setShowPointsInfo(false)}
        >
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b bg-orange-500 p-5 text-white">
              <div>
                <h2 className="flex items-center gap-2 text-xl font-bold">
                  <Award size={22} />
                  Driver Point System Rules
                </h2>

                <p className="mt-1 text-sm text-orange-50">
                  This explains how driver performance points are calculated.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowPointsInfo(false)}
                className="rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[76vh] overflow-y-auto bg-gray-50 p-5">
              {masterPointsLoading ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {[1, 2, 3, 4].map((item) => (
                    <div
                      key={item}
                      className="h-40 animate-pulse rounded-2xl border bg-white"
                    />
                  ))}
                </div>
              ) : masterPointsData.length === 0 ? (
                <div className="rounded-2xl border border-dashed bg-white p-8 text-center">
                  <Info className="mx-auto mb-3 h-10 w-10 text-gray-300" />

                  <h3 className="text-lg font-semibold text-gray-700">
                    No point rules found
                  </h3>

                  <p className="text-sm text-gray-500">
                    Point system data is not available right now.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {masterPointsData.map((factor) => {
                    const meta = getFactorMeta(factor.factor_code);
                    const criteria = factor.performmancePointsCriteria || [];

                    return (
                      <div
                        key={factor.factor_code}
                        className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                      >
                        <div className="border-b bg-gray-100 p-4">
                          <div className="flex items-start gap-3">
                            <div className="rounded-xl bg-orange-500 p-2 text-white">
                              {meta.icon}
                            </div>

                            <div>
                              <h3 className="text-base font-bold text-gray-900">
                                {meta.label}
                              </h3>

                              <p className="mt-0.5 text-xs text-gray-500">
                                {meta.desc}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="p-3">
                          <div className="overflow-hidden rounded-xl border">
                            <table className="min-w-full text-sm">
                              <thead className="bg-orange-500 text-left text-xs uppercase tracking-wide text-white">
                                <tr>
                                  <th className="px-3 py-2">Condition</th>
                                  <th className="px-3 py-2 text-right">
                                    Points
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {criteria.map((rule) => {
                                  const tone = getPointTone(rule.points);
                                  const value = Number(rule.points || 0);

                                  return (
                                    <tr
                                      key={rule.event_code}
                                      className="border-t bg-white hover:bg-orange-50"
                                    >
                                      <td className="px-3 py-2">
                                        <p className="font-medium text-gray-800">
                                          {rule.condition_text}
                                        </p>

                                        <p className="text-xs text-gray-400">
                                          {rule.event_code}
                                        </p>
                                      </td>

                                      <td className="px-3 py-2 text-right">
                                        <span
                                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${tone.bg} ${tone.border} ${tone.text}`}
                                        >
                                          {tone.icon}
                                          {value > 0 ? `+${value}` : value}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverPointsSection;
